/**
 * Service de cache avec isolation par tenant
 * 
 * Ce service permet de mettre en cache des données tout en garantissant
 * l'isolation entre les différents tenants. Chaque clé de cache est préfixée
 * par l'ID du tenant pour éviter les collisions et les fuites de données.
 */

import tenantManager from './tenant-manager';

// Interface pour les entrées de cache
interface CacheEntry<T> {
  value: T;
  expiresAt: number | null; // Timestamp d'expiration ou null si pas d'expiration
}

export class TenantCacheService {
  private cache: Map<string, CacheEntry<any>> = new Map();
  
  /**
   * Génère une clé de cache préfixée par le tenant
   */
  private getKey(tenantId: string, key: string): string {
    return `tenant:${tenantId}:${key}`;
  }
  
  /**
   * Récupère une valeur du cache
   */
  get<T>(tenantId: string, key: string): T | null {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(tenantId)) {
      console.warn(`Tentative d'accès au cache pour un tenant inaccessible: ${tenantId}`);
      return null;
    }
    
    const cacheKey = this.getKey(tenantId, key);
    const entry = this.cache.get(cacheKey);
    
    // Si l'entrée n'existe pas
    if (!entry) {
      return null;
    }
    
    // Vérifier si l'entrée a expiré
    if (entry.expiresAt !== null && entry.expiresAt < Date.now()) {
      this.cache.delete(cacheKey);
      return null;
    }
    
    return entry.value as T;
  }
  
  /**
   * Stocke une valeur dans le cache
   * @param ttl Durée de vie en secondes (null pour pas d'expiration)
   */
  set<T>(tenantId: string, key: string, value: T, ttl?: number): void {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(tenantId)) {
      console.warn(`Tentative d'écriture dans le cache pour un tenant inaccessible: ${tenantId}`);
      return;
    }
    
    const cacheKey = this.getKey(tenantId, key);
    const expiresAt = ttl ? Date.now() + ttl * 1000 : null;
    
    this.cache.set(cacheKey, {
      value,
      expiresAt
    });
    
    console.log(`[Cache] Valeur mise en cache pour le tenant ${tenantId}: ${key}`);
  }
  
  /**
   * Supprime une valeur du cache
   */
  delete(tenantId: string, key: string): boolean {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(tenantId)) {
      console.warn(`Tentative de suppression du cache pour un tenant inaccessible: ${tenantId}`);
      return false;
    }
    
    const cacheKey = this.getKey(tenantId, key);
    return this.cache.delete(cacheKey);
  }
  
  /**
   * Vide le cache pour un tenant spécifique
   */
  clearTenantCache(tenantId: string): void {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(tenantId)) {
      console.warn(`Tentative de vidage du cache pour un tenant inaccessible: ${tenantId}`);
      return;
    }
    
    const prefix = `tenant:${tenantId}:`;
    
    // Supprimer toutes les entrées pour ce tenant
    for (const key of Array.from(this.cache.keys())) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
    
    console.log(`[Cache] Cache vidé pour le tenant ${tenantId}`);
  }
  
  /**
   * Récupère une valeur du cache ou l'initialise si elle n'existe pas
   */
  async getOrSet<T>(
    tenantId: string, 
    key: string, 
    factory: () => Promise<T>, 
    ttl?: number
  ): Promise<T> {
    // Essayer de récupérer depuis le cache
    const cachedValue = this.get<T>(tenantId, key);
    
    // Si la valeur existe dans le cache, la retourner
    if (cachedValue !== null) {
      return cachedValue;
    }
    
    // Sinon, appeler la fonction factory pour générer la valeur
    try {
      const value = await factory();
      
      // Mettre en cache la nouvelle valeur
      this.set(tenantId, key, value, ttl);
      
      return value;
    } catch (error) {
      console.error(`[Cache] Erreur lors de la génération de la valeur pour ${key}:`, error);
      throw error;
    }
  }
}

// Exporter une instance unique du service
export const tenantCacheService = new TenantCacheService();

export default tenantCacheService;
